import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const testimonials = [
    {
      quote: "A modern lounge that feels intentional, calm, and perfectly designed for conversation.",
      name: "Marcus Aurelius",
      title: "Lounge Guest",
      avatar: "https://public.readdy.ai/ai/img_res/33f0f988599fa5f5820d89a4be6fa6ff.jpg"
    },
    {
      quote: "An elegant space that blends atmosphere and culture effortlessly.",
      name: "Nefertari Jones",
      title: "Creative Director",
      avatar: "https://readdy.ai/api/search-image?query=elegant-african-american-woman-with-regal-presence-wearing-gold-jewelry-warm-sophisticated-lighting-portrait-photography&width=120&height=120&seq=testimonial2&orientation=squarish"
    },
    {
      quote: "The attention to detail is remarkable. Every element contributes to an atmosphere of sophistication and tranquility.",
      name: "David Chen",
      title: "Business Executive",
      avatar: "https://readdy.ai/api/search-image?query=professional-asian-businessman-in-tailored-suit-confident-smile-executive-portrait-warm-studio-lighting-sophisticated-appearance&width=120&height=120&seq=testimonial3asian&orientation=squarish"
    },
    {
      quote: "Sacred Ember provides the perfect environment for meaningful networking. It's where business meets culture.",
      name: "Isabella Rodriguez",
      title: "Entrepreneur",
      avatar: "https://readdy.ai/api/search-image?query=elegant-latina-businesswoman-professional-attire-warm-smile-confident-expression-studio-portrait-sophisticated-lighting&width=120&height=120&seq=testimonial4latina&orientation=squarish"
    },
    {
      quote: "A sanctuary for creative minds. The ambiance inspires innovation and thoughtful collaboration.",
      name: "James Morrison",
      title: "Creative Strategist",
      avatar: "https://readdy.ai/api/search-image?query=professional-caucasian-man-creative-strategist-stylish-casual-business-attire-confident-expression-warm-studio-lighting-contemporary-portrait-artistic-professional&width=120&height=120&seq=testimonial5caucasian&orientation=squarish"
    },
    {
      quote: "The herbal cigars and curated atmosphere create an experience unlike any other lounge I've visited.",
      name: "Amara Williams",
      title: "Cultural Curator",
      avatar: "https://readdy.ai/api/search-image?query=sophisticated-african-american-woman-with-natural-hair-elegant-jewelry-warm-lighting-professional-portrait-confident-expression&width=120&height=120&seq=testimonial6african&orientation=squarish"
    }
  ];

  const features = [
    {
      icon: "ri-briefcase-line",
      title: "Business Conversations & Professional Networking",
      description: "A premium space for professional discussions and meaningful connections"
    },
    {
      icon: "ri-lightbulb-flash-line",
      title: "Creative Brainstorming & Collaboration",
      description: "Thoughtfully designed environment to inspire innovation and collaboration"
    },
    {
      icon: "ri-contrast-drop-2-line",
      title: "Relaxation & Mental Reset",
      description: "Calm atmosphere for unwinding, reflection, and mental clarity"
    },
    {
      icon: "ri-vip-crown-line",
      title: "Curated Social Gatherings",
      description: "Exclusive networking opportunities for aligned professionals and creatives"
    },
    {
      icon: "ri-ancient-pavilion-line",
      title: "Cultural & Artistic Events",
      description: "Curated events celebrating culture, art, and creative expression"
    },
    {
      icon: "ri-fire-line",
      title: "Sacred Herbal Cigars",
      description: "Tobacco-free botanical blends crafted for aromatic enjoyment and atmosphere"
    }
  ];

  const membershipCircles = [
    {
      icon: "ri-fire-line",
      title: "Core Circle",
      description: "For members entering the Sacred Ember Lounge experience.",
      benefits: [
        "Private lounge access",
        "Member-only events",
        "Priority reservations"
      ]
    },
    {
      icon: "ri-vip-crown-line",
      title: "Elite Circle",
      description: "For professionals and frequent guests seeking an elevated level of service.",
      benefits: [
        "VIP areas",
        "Curated experiences",
        "Enhanced services"
      ]
    },
    {
      icon: "ri-contrast-drop-2-fill",
      title: "Executive Circle",
      subtitle: "(Invitation Only)",
      description: "For long-term partners and premium members.",
      benefits: [
        "Full venue access",
        "Concierge-level privileges",
        "Exclusive offerings"
      ]
    }
  ];

  const foodMenu = [
    {
      category: "Small Plates",
      items: [
        { name: "Charcuterie Board", description: "Curated selection of artisan cheeses, cured meats, olives, and crackers", price: "$24" },
        { name: "Hummus & Flatbread", description: "House-made hummus with warm pita and fresh vegetables", price: "$12" },
        { name: "Wings", description: "Crispy wings with choice of sauce: buffalo, honey garlic, or BBQ", price: "$16" }
      ]
    },
    {
      category: "Mains",
      items: [
        { name: "Gourmet Burger", description: "Premium beef patty, aged cheddar, caramelized onions, special sauce", price: "$18" },
        { name: "Grilled Chicken Sandwich", description: "Herb-marinated chicken breast, avocado, arugula, chipotle aioli", price: "$16" },
        { name: "Pasta Primavera", description: "Fresh seasonal vegetables, garlic, olive oil, parmesan", price: "$17" }
      ]
    },
    {
      category: "Desserts",
      items: [
        { name: "Chocolate Lava Cake", description: "Warm chocolate cake with molten center, vanilla ice cream", price: "$10" },
        { name: "Cheesecake", description: "Classic New York style with seasonal fruit compote", price: "$9" }
      ]
    }
  ];

  const beverages = [
    { name: "Craft Cocktails", price: "$14-18" },
    { name: "Premium Spirits", price: "$12-25" },
    { name: "Wine Selection", price: "$10-16/glass" },
    { name: "Craft Beer", price: "$8-12" },
    { name: "Specialty Coffee", price: "$5-8" },
    { name: "Fresh Juices", price: "$6-9" }
  ];

  return (
    <div className="min-h-screen bg-[#0A0E27] text-[#F5F1E8]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A0E27]/95 backdrop-blur-md shadow-lg shadow-[#D4AF37]/10' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/dadfa7b8e8bbab8ae3e8df395caac6f8/bbfc43b97cf1f83227ff7a4f04afe222.png" alt="Sacred Ember Lounge" className="h-20 w-auto" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#experience" className="text-sm tracking-widest uppercase text-[#D4AF37] hover:text-[#F5F1E8] transition-colors duration-300 cursor-pointer whitespace-nowrap">Experience</a>
            <a href="#atmosphere" className="text-sm tracking-widest uppercase text-[#D4AF37] hover:text-[#F5F1E8] transition-colors duration-300 cursor-pointer whitespace-nowrap">Atmosphere</a>
            <a href="#menu" className="text-sm tracking-widest uppercase text-[#D4AF37] hover:text-[#F5F1E8] transition-colors duration-300 cursor-pointer whitespace-nowrap">Menu</a>
            <a href="#entertainment" className="text-sm tracking-widest uppercase text-[#D4AF37] hover:text-[#F5F1E8] transition-colors duration-300 cursor-pointer whitespace-nowrap">Entertainment</a>
            <a href="#membership" className="text-sm tracking-widest uppercase text-[#D4AF37] hover:text-[#F5F1E8] transition-colors duration-300 cursor-pointer whitespace-nowrap">Membership</a>
            <a href="#contact" className="text-sm tracking-widest uppercase text-[#D4AF37] hover:text-[#F5F1E8] transition-colors duration-300 cursor-pointer whitespace-nowrap">Connect</a>
          </div>
          <button className="md:hidden text-[#D4AF37] w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-menu-line text-2xl"></i>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=luxurious-dimly-lit-cigar-lounge-interior-with-golden-candlelight-rich-leather-seating-ambient-smoke-wisps-floating-in-warm-atmospheric-lighting-elegant-sophisticated-ambiance-dark-walls-with-gold-accents-mystical-mood&width=1920&height=1080&seq=hero1&orientation=landscape" 
            alt="Sacred Ember Lounge Interior" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E27]/95 via-[#0A0E27]/70 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-2xl">
            <div className="w-1 h-32 bg-gradient-to-b from-[#D4AF37] to-transparent mb-8"></div>
            <h1 className="font-serif text-7xl md:text-8xl font-bold mb-6 bg-gradient-to-br from-[#D4AF37] via-[#F5D76E] to-[#B8956A] bg-clip-text text-transparent leading-tight" style={{ letterSpacing: '0.05em' }}>
              SACRED EMBER LOUNGE™
            </h1>
            <p className="font-serif italic text-2xl md:text-3xl text-[#B8956A] mb-12">
              Where Atmosphere, Culture, and Experience Converge
            </p>
            <p className="text-lg md:text-xl text-[#F5F1E8]/90 mb-6 leading-relaxed max-w-xl">
              A private members lounge built for professionals, creatives, and guests who value atmosphere, privacy, and meaningful connection.
            </p>
            <p className="text-lg md:text-xl text-[#F5F1E8]/90 mb-12 leading-relaxed max-w-xl">
              It reflects a sovereign standard of quality and control — meaning we maintain independent, high-level standards for design, service, and guest experience.
            </p>
            <a href="#experience" className="inline-block px-12 py-5 bg-[#D4AF37] text-[#0A0E27] rounded-full font-semibold text-lg tracking-wide hover:bg-[#F5D76E] hover:shadow-lg hover:shadow-[#D4AF37]/50 transition-all duration-300 cursor-pointer whitespace-nowrap">
              EXPLORE THE EXPERIENCE
            </a>
          </div>
        </div>

        {/* Floating Logo Watermark */}
        <div className="absolute bottom-12 right-12 opacity-40 hidden lg:block">
          <img src="https://static.readdy.ai/image/dadfa7b8e8bbab8ae3e8df395caac6f8/bbfc43b97cf1f83227ff7a4f04afe222.png" alt="Sacred Ember" className="w-64 h-auto" />
        </div>
      </section>

      {/* Experience Features */}
      <section id="experience" className="py-32 px-6 bg-[#0A0E27]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#D4AF37] mb-6">The Sacred Ember Experience</h2>
            <p className="text-xl text-[#B8956A] font-serif italic mb-8">More than a lounge</p>
            <p className="text-lg text-[#F5F1E8] max-w-2xl mx-auto">
              Sacred Ember is a thoughtfully designed environment for focus, creativity, and relaxation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`rounded-3xl p-10 text-center transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer ${
                  index % 3 === 0 ? 'bg-[#0A0E27] border-2 border-[#D4AF37] hover:shadow-[#D4AF37]/30' :
                  index % 3 === 1 ? 'bg-[#D4AF37] text-[#0A0E27] hover:shadow-[#D4AF37]/50 md:mt-12' :
                  'bg-[#0A0E27]/50 border-2 border-[#D4AF37]/50 hover:shadow-[#D4AF37]/20'
                }`}
                style={{ aspectRatio: '3/4' }}
              >
                <div className={`w-16 h-16 mx-auto mb-6 flex items-center justify-center ${index % 3 === 1 ? 'text-[#0A0E27]' : 'text-[#D4AF37]'}`}>
                  <i className={`${feature.icon} text-6xl`}></i>
                </div>
                <h3 className={`text-2xl font-bold mb-4 uppercase tracking-wide ${index % 3 === 1 ? 'text-[#0A0E27]' : 'text-[#D4AF37]'}`}>
                  {feature.title}
                </h3>
                <p className={`font-serif text-base leading-relaxed ${index % 3 === 1 ? 'text-[#0A0E27]/80' : 'text-[#F5F1E8]'}`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Atmosphere & Design Section */}
      <section id="atmosphere" className="relative py-32 px-6" style={{ background: 'linear-gradient(180deg, #0A0E27 0%, #0D1235 100%)' }}>
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <i className="ri-contrast-drop-2-fill text-5xl text-[#D4AF37] mb-8 inline-block"></i>
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#D4AF37] mb-6">Atmosphere & Design</h2>
          </div>

          <div className="space-y-16 text-center">
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent hidden md:block"></div>
              <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent hidden md:block"></div>
              
              <p className="font-serif text-3xl md:text-4xl leading-relaxed text-[#F5F1E8]" style={{ lineHeight: '1.8' }}>
                Gold-tone interiors.<br />
                Low-light ambiance.<br />
                Curated soundscapes.<br />
                Signature architectural centerpiece.
              </p>
            </div>

            <div className="h-px w-32 bg-[#D4AF37] mx-auto"></div>

            <p className="font-serif text-3xl md:text-4xl leading-relaxed text-[#F5F1E8]" style={{ lineHeight: '1.8' }}>
              Creating a visual and sensory environment that <em className="text-[#D4AF37]">supports conversation and relaxation</em>.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Showcase */}
      <section id="gallery" className="py-32 px-6 bg-gradient-to-b from-[#0A0E27] to-[#0D1235]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#D4AF37] mb-6">The Atmosphere</h2>
            <p className="text-xl text-[#B8956A] font-serif italic">Where atmosphere becomes experience</p>
          </div>

          <div className="grid grid-cols-12 gap-4">
            {/* Large Left Image */}
            <div className="col-span-12 md:col-span-7 row-span-2">
              <div className="relative rounded-xl overflow-hidden border border-[#D4AF37] group cursor-pointer h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=luxurious-cigar-lounge-interior-with-rich-leather-seating-golden-ambient-lighting-dark-elegant-walls-with-gold-trim-sophisticated-atmosphere-warm-candlelight-mystical-ambiance-high-end-furniture&width=800&height=1000&seq=gallery1&orientation=portrait" 
                  alt="Sacred Ember Interior" 
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#0A0E27]/0 group-hover:bg-[#0A0E27]/80 transition-all duration-300 flex items-center justify-center">
                  <p className="text-[#D4AF37] font-serif text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">Sacred Space</p>
                </div>
              </div>
            </div>

            {/* Top Right Image */}
            <div className="col-span-12 md:col-span-5">
              <div className="relative rounded-xl overflow-hidden border border-[#D4AF37] group cursor-pointer h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=close-up-of-premium-herbal-cigar-with-glowing-ember-tip-warm-golden-light-smoke-wisps-rising-elegant-presentation-dark-background-with-gold-accents-mystical-atmosphere&width=600&height=400&seq=gallery2&orientation=landscape" 
                  alt="Sacred Herbal Cigar" 
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#0A0E27]/0 group-hover:bg-[#0A0E27]/80 transition-all duration-300 flex items-center justify-center">
                  <p className="text-[#D4AF37] font-serif text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">Sacred Ember</p>
                </div>
              </div>
            </div>

            {/* Middle Right Image */}
            <div className="col-span-12 md:col-span-5">
              <div className="relative rounded-xl overflow-hidden border border-[#D4AF37] group cursor-pointer h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=private-luxury-booth-with-plush-leather-seating-gold-accents-intimate-lighting-elegant-table-setting-sophisticated-lounge-atmosphere-dark-walls-with-golden-trim&width=600&height=400&seq=gallery3&orientation=landscape" 
                  alt="Private Elite Section" 
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#0A0E27]/0 group-hover:bg-[#0A0E27]/80 transition-all duration-300 flex items-center justify-center">
                  <p className="text-[#D4AF37] font-serif text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">Elite Sanctuary</p>
                </div>
              </div>
            </div>

            {/* Bottom Right Image */}
            <div className="col-span-12 md:col-span-5">
              <div className="relative rounded-xl overflow-hidden border border-[#D4AF37] group cursor-pointer h-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=mystical-altar-with-sacred-flame-burning-golden-light-ancient-egyptian-symbols-dark-ceremonial-setting-spiritual-atmosphere-warm-candlelight-ritual-space&width=600&height=400&seq=gallery4&orientation=landscape" 
                  alt="Sacred Altar" 
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#0A0E27]/0 group-hover:bg-[#0A0E27]/80 transition-all duration-300 flex items-center justify-center">
                  <p className="text-[#D4AF37] font-serif text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">Sacred Flame</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sacred Herbal Cigars Section */}
      <section className="py-32 px-6 bg-[#0A0E27]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <i className="ri-fire-fill text-7xl text-[#D4AF37]"></i>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#D4AF37] mb-8">Sacred Herbal Cigars™</h2>
          <div className="space-y-6 text-xl md:text-2xl font-serif text-[#F5F1E8] leading-relaxed">
            <p>Our herbal cigars feature tobacco-free botanical blends, created for aroma, ambiance, and a smooth sensory experience.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-2xl p-8">
              <i className="ri-leaf-line text-4xl text-[#D4AF37] mb-4 inline-block"></i>
              <h3 className="text-xl font-bold text-[#D4AF37] mb-3">Tobacco-Free Blends</h3>
              <p className="text-[#F5F1E8] font-serif">Pure botanical blends with no tobacco or harmful additives</p>
            </div>
            <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-2xl p-8">
              <i className="ri-ancient-pavilion-line text-4xl text-[#D4AF37] mb-4 inline-block"></i>
              <h3 className="text-xl font-bold text-[#D4AF37] mb-3">Global Herbal Traditions</h3>
              <p className="text-[#F5F1E8] font-serif">Crafted with inspiration from global herbal traditions</p>
            </div>
            <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-2xl p-8">
              <i className="ri-contrast-drop-2-line text-4xl text-[#D4AF37] mb-4 inline-block"></i>
              <h3 className="text-xl font-bold text-[#D4AF37] mb-3">Sensory Experience</h3>
              <p className="text-[#F5F1E8] font-serif">Designed for relaxed enjoyment in a refined environment</p>
            </div>
          </div>
          <div className="mt-16 h-px w-64 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto"></div>
          <p className="mt-16 text-xl md:text-2xl font-serif text-[#B8956A] italic">
            Sacred Ember prioritizes quality, design, and guest comfort.
          </p>
        </div>
      </section>

      {/* Food & Beverage Menu Section */}
      <section id="menu" className="py-32 px-6 bg-gradient-to-b from-[#0D1235] to-[#0A0E27]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="mb-8">
              <i className="ri-restaurant-2-line text-6xl text-[#D4AF37]"></i>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#D4AF37] mb-6">Culinary Offerings</h2>
            <p className="text-xl text-[#B8956A] font-serif italic mb-4">Thoughtfully prepared, elegantly served</p>
            <p className="text-lg text-[#F5F1E8] max-w-2xl mx-auto">
              Complementing the Sacred Ember experience with quality food and beverages crafted for enjoyment and conversation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Food Menu */}
            <div>
              <h3 className="font-serif text-3xl font-bold text-[#D4AF37] mb-8 text-center">Food Menu</h3>
              <div className="space-y-8">
                {foodMenu.map((section, index) => (
                  <div key={index} className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-2xl p-8">
                    <h4 className="text-2xl font-bold text-[#D4AF37] mb-6 uppercase tracking-wide">{section.category}</h4>
                    <div className="space-y-5">
                      {section.items.map((item, idx) => (
                        <div key={idx} className="border-b border-[#D4AF37]/30 pb-4 last:border-0 last:pb-0">
                          <div className="flex justify-between items-start mb-2">
                            <h5 className="text-lg font-semibold text-[#F5F1E8]">{item.name}</h5>
                            <span className="text-lg font-bold text-[#D4AF37] ml-4 whitespace-nowrap">{item.price}</span>
                          </div>
                          <p className="text-sm text-[#F5F1E8]/70 font-serif">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Beverages */}
            <div>
              <h3 className="font-serif text-3xl font-bold text-[#D4AF37] mb-8 text-center">Beverages</h3>
              <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-2xl p-8 h-fit sticky top-32">
                <div className="mb-8">
                  <div className="w-full h-64 rounded-xl overflow-hidden mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=elegant-cocktail-bar-setup-with-premium-spirits-bottles-golden-lighting-sophisticated-lounge-atmosphere-craft-cocktails-wine-glasses-dark-background-with-gold-accents-luxurious-beverage-display&width=600&height=400&seq=beverages1&orientation=landscape" 
                      alt="Premium Beverages" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <p className="text-[#F5F1E8] font-serif text-center mb-6">
                    Curated selection of premium beverages to enhance your experience
                  </p>
                </div>
                <div className="space-y-4">
                  {beverages.map((beverage, index) => (
                    <div key={index} className="flex justify-between items-center border-b border-[#D4AF37]/30 pb-3 last:border-0 last:pb-0">
                      <span className="text-[#F5F1E8] font-semibold">{beverage.name}</span>
                      <span className="text-[#D4AF37] font-bold whitespace-nowrap ml-4">{beverage.price}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-[#D4AF37]/30">
                  <p className="text-sm text-[#B8956A] font-serif italic text-center">
                    Full menu available upon reservation
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Menu Note */}
          <div className="bg-gradient-to-br from-[#D4AF37] to-[#B8956A] rounded-2xl p-8 text-center text-[#0A0E27]">
            <i className="ri-information-line text-4xl mb-4 inline-block"></i>
            <p className="text-lg font-serif">
              Menu items available for members and guests by reservation. Dietary accommodations available upon request.
            </p>
          </div>
        </div>
      </section>

      {/* Entertainment & Events Section */}
      <section id="entertainment" className="py-32 px-6 bg-[#0A0E27]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="mb-8">
              <i className="ri-music-2-line text-6xl text-[#D4AF37]"></i>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#D4AF37] mb-6">Live Entertainment & Events</h2>
            <p className="text-xl text-[#B8956A] font-serif italic mb-4">Where culture comes alive</p>
            <p className="text-lg text-[#F5F1E8] max-w-2xl mx-auto">
              Sacred Ember hosts curated entertainment experiences that elevate the atmosphere and celebrate artistic expression.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Live Jazz */}
            <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-3xl p-10 hover:scale-105 transition-all duration-300 cursor-pointer hover:shadow-2xl hover:shadow-[#D4AF37]/30">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-music-2-fill text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-4 text-center uppercase tracking-wide">Live Jazz</h3>
              <p className="text-[#F5F1E8] font-serif text-center leading-relaxed">
                Smooth jazz performances creating the perfect backdrop for conversation and relaxation
              </p>
            </div>

            {/* R&B Nights */}
            <div className="bg-[#D4AF37] rounded-3xl p-10 hover:scale-105 transition-all duration-300 cursor-pointer hover:shadow-2xl hover:shadow-[#D4AF37]/50 md:mt-8">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center text-[#0A0E27]">
                <i className="ri-headphone-line text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#0A0E27] mb-4 text-center uppercase tracking-wide">R&B Nights</h3>
              <p className="text-[#0A0E27]/80 font-serif text-center leading-relaxed">
                Soulful R&B performances featuring talented vocalists and musicians
              </p>
            </div>

            {/* Comedy Shows */}
            <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-3xl p-10 hover:scale-105 transition-all duration-300 cursor-pointer hover:shadow-2xl hover:shadow-[#D4AF37]/30">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-emotion-laugh-line text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-4 text-center uppercase tracking-wide">Comedy Shows</h3>
              <p className="text-[#F5F1E8] font-serif text-center leading-relaxed">
                Curated comedy performances bringing laughter and entertainment to the lounge
              </p>
            </div>

            {/* Talent Showcases */}
            <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-3xl p-10 hover:scale-105 transition-all duration-300 cursor-pointer hover:shadow-2xl hover:shadow-[#D4AF37]/30">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-star-line text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-4 text-center uppercase tracking-wide">Talent Shows</h3>
              <p className="text-[#F5F1E8] font-serif text-center leading-relaxed">
                Open mic and talent showcase nights celebrating emerging artists and performers
              </p>
            </div>

            {/* Karaoke Nights */}
            <div className="bg-[#D4AF37] rounded-3xl p-10 hover:scale-105 transition-all duration-300 cursor-pointer hover:shadow-2xl hover:shadow-[#D4AF37]/50 md:mt-8">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center text-[#0A0E27]">
                <i className="ri-mic-line text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#0A0E27] mb-4 text-center uppercase tracking-wide">Karaoke Nights</h3>
              <p className="text-[#0A0E27]/80 font-serif text-center leading-relaxed">
                Interactive karaoke experiences for members and guests to showcase their vocal talents
              </p>
            </div>

            {/* Fashion Shows */}
            <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-3xl p-10 hover:scale-105 transition-all duration-300 cursor-pointer hover:shadow-2xl hover:shadow-[#D4AF37]/30">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-shirt-line text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-4 text-center uppercase tracking-wide">Fashion Shows</h3>
              <p className="text-[#F5F1E8] font-serif text-center leading-relaxed">
                Exclusive fashion presentations featuring designers and cultural style expressions
              </p>
            </div>
          </div>

          {/* Entertainment Image Showcase */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37] group cursor-pointer h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=diverse-multicultural-jazz-band-performing-in-luxurious-dark-lounge-african-american-saxophone-player-asian-female-pianist-latino-drummer-middle-eastern-bass-player-caucasian-vocalist-mixed-ethnicities-golden-warm-lighting-sophisticated-atmosphere-dark-elegant-background-with-gold-accents-inclusive-diverse-audience-watching&width=800&height=600&seq=entertainmentjazzdiversity&orientation=landscape" 
                alt="Live Jazz Performance" 
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-[#0A0E27]/0 group-hover:bg-[#0A0E27]/80 transition-all duration-300 flex items-center justify-center">
                <p className="text-[#D4AF37] font-serif text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">Live Music</p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37] group cursor-pointer h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=diverse-multicultural-fashion-runway-show-in-luxury-lounge-african-american-female-model-asian-male-model-latina-model-middle-eastern-model-caucasian-model-mixed-race-models-walking-runway-various-skin-tones-cultural-fashion-styles-golden-elegant-lighting-diverse-audience-dark-background-with-gold-accents-inclusive-celebration&width=800&height=600&seq=entertainmentfashiondiversity&orientation=landscape" 
                alt="Fashion Show Event" 
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-[#0A0E27]/0 group-hover:bg-[#0A0E27]/80 transition-all duration-300 flex items-center justify-center">
                <p className="text-[#D4AF37] font-serif text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">Cultural Events</p>
              </div>
            </div>
          </div>

          {/* Event Schedule CTA */}
          <div className="bg-gradient-to-br from-[#D4AF37] to-[#B8956A] rounded-3xl p-12 text-center text-[#0A0E27]">
            <i className="ri-calendar-event-line text-5xl mb-6 inline-block"></i>
            <h3 className="font-serif text-3xl md:text-4xl font-bold mb-6">
              Check Our Event Calendar
            </h3>
            <p className="text-xl font-serif mb-8 max-w-2xl mx-auto">
              Entertainment schedules vary by season. Contact us for upcoming events and private booking opportunities.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="mailto:info@sacredemberlounge.com" className="inline-block px-10 py-4 bg-[#0A0E27] text-[#D4AF37] rounded-full font-bold text-lg tracking-wide hover:bg-[#0A0E27]/90 transition-all duration-300 cursor-pointer whitespace-nowrap">
                INQUIRE ABOUT EVENTS
              </a>
              <a href="tel:+18447122810" className="inline-block px-10 py-4 bg-transparent border-2 border-[#0A0E27] text-[#0A0E27] rounded-full font-bold text-lg tracking-wide hover:bg-[#0A0E27] hover:text-[#D4AF37] transition-all duration-300 cursor-pointer whitespace-nowrap">
                CALL US
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 px-6 bg-gradient-to-b from-[#0D1235] to-[#0A0E27]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#D4AF37] mb-6">Guest Impressions</h2>
            <p className="text-xl text-[#B8956A] font-serif italic">What our community says</p>
          </div>

          <div className="relative">
            <div className="overflow-hidden">
              <div className="flex gap-8 transition-transform duration-500" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="min-w-full md:min-w-[calc(50%-1rem)] flex-shrink-0">
                    <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-3xl p-10 hover:rotate-1 transition-all duration-300 cursor-pointer h-full">
                      <i className="ri-fire-fill text-3xl text-[#D4AF37] mb-6 inline-block"></i>
                      <p className="font-serif italic text-xl md:text-2xl text-[#F5F1E8] mb-8 leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#D4AF37] flex-shrink-0">
                          <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-lg text-[#D4AF37]">{testimonial.name}</p>
                          <p className="text-sm text-[#F5F1E8]/70 font-serif">{testimonial.title}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center gap-3 mt-12">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                    activeTestimonial === index ? 'bg-[#D4AF37] shadow-lg shadow-[#D4AF37]/50 w-8' : 'bg-[#D4AF37]/30'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section id="membership" className="py-32 px-6 bg-[#0A0E27]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="mb-8">
              <i className="ri-vip-crown-fill text-6xl text-[#D4AF37]"></i>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#D4AF37] mb-8">Membership Program</h2>
            <p className="text-xl md:text-2xl text-[#F5F1E8] font-serif leading-relaxed max-w-3xl mx-auto mb-6">
              Sacred Ember Lounge™ operates on a private membership model to maintain privacy, quality, and consistent experience.
            </p>
          </div>

          {/* Why Membership */}
          <div className="bg-[#0A0E27]/50 border-2 border-[#D4AF37] rounded-3xl p-12 mb-16">
            <div className="text-center mb-8">
              <i className="ri-shield-check-fill text-5xl text-[#D4AF37] mb-6 inline-block"></i>
            </div>
            
            <p className="font-serif text-2xl md:text-3xl text-[#F5F1E8] text-center leading-relaxed mb-8">
              Membership allows us to:
            </p>

            <div className="max-w-2xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-center gap-4 justify-center">
                  <i className="ri-sparkling-fill text-2xl text-[#D4AF37]"></i>
                  <span className="text-lg text-[#F5F1E8] font-serif">Preserve a premium atmosphere</span>
                </div>
                <div className="flex items-center gap-4 justify-center">
                  <i className="ri-sparkling-fill text-2xl text-[#D4AF37]"></i>
                  <span className="text-lg text-[#F5F1E8] font-serif">Avoid overcrowding</span>
                </div>
                <div className="flex items-center gap-4 justify-center">
                  <i className="ri-sparkling-fill text-2xl text-[#D4AF37]"></i>
                  <span className="text-lg text-[#F5F1E8] font-serif">Deliver a focused guest experience</span>
                </div>
              </div>
            </div>
          </div>

          {/* Membership Tiers */}
          <div className="mb-20">
            <h3 className="font-serif text-4xl md:text-5xl font-bold text-[#D4AF37] text-center mb-16">
              Membership Tiers
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {membershipCircles.map((circle, index) => (
                <div 
                  key={index}
                  className={`rounded-3xl p-10 transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer ${
                    index === 0 ? 'bg-[#0A0E27]/50 border-2 border-[#D4AF37] hover:shadow-[#D4AF37]/30' :
                    index === 1 ? 'bg-[#D4AF37] text-[#0A0E27] hover:shadow-[#D4AF37]/50 md:mt-8' :
                    'bg-gradient-to-br from-[#D4AF37] to-[#B8956A] text-[#0A0E27] hover:shadow-[#D4AF37]/60 md:mt-16'
                  }`}
                >
                  <div className={`w-16 h-16 mx-auto mb-6 flex items-center justify-center ${index === 0 ? 'text-[#D4AF37]' : 'text-[#0A0E27]'}`}>
                    <i className={`${circle.icon} text-6xl`}></i>
                  </div>
                  <h4 className={`text-2xl font-bold mb-2 uppercase tracking-wide text-center ${index === 0 ? 'text-[#D4AF37]' : 'text-[#0A0E27]'}`}>
                    {circle.title}
                  </h4>
                  {circle.subtitle && (
                    <p className="text-center text-sm mb-4 font-serif italic text-[#0A0E27]/80">
                      {circle.subtitle}
                    </p>
                  )}
                  <p className={`font-serif text-base leading-relaxed mb-6 text-center ${index === 0 ? 'text-[#F5F1E8]' : 'text-[#0A0E27]'}`}>
                    {circle.description}
                  </p>
                  <div className="space-y-3">
                    {circle.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <i className={`ri-check-line text-xl mt-1 flex-shrink-0 ${index === 0 ? 'text-[#D4AF37]' : 'text-[#0A0E27]'}`}></i>
                        <span className={`font-serif ${index === 0 ? 'text-[#F5F1E8]' : 'text-[#0A0E27]'}`}>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* How to Inquire */}
          <div className="bg-gradient-to-br from-[#D4AF37] to-[#B8956A] rounded-3xl p-12 text-center text-[#0A0E27]">
            <i className="ri-lock-unlock-line text-6xl mb-6 inline-block"></i>
            <h3 className="font-serif text-3xl md:text-4xl font-bold mb-8">
              Contact & Reservations
            </h3>
            
            <p className="text-xl font-serif mb-6">For membership inquiries or private reservations:</p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center gap-3">
                <i className="ri-mail-line text-2xl"></i>
                <a 
                  href="mailto:info@sacredemberlounge.com" 
                  className="text-xl font-serif hover:underline"
                >
                  info@sacredemberlounge.com
                </a>
              </div>
              
              <div className="flex items-center justify-center gap-3">
                <i className="ri-phone-line text-2xl"></i>
                <a 
                  href="tel:+18447122810" 
                  className="text-xl font-serif hover:underline"
                >
                  844-712-2810 ext. 3
                </a>
              </div>
              
              <div className="flex items-center justify-center gap-3">
                <i className="ri-map-pin-line text-2xl"></i>
                <p className="text-lg font-serif">
                  2935 E. Main Street, Unit 91133<br />
                  Bexley, Ohio 43209, USA
                </p>
              </div>
            </div>
            
            <div className="h-px w-48 bg-[#0A0E27]/30 mx-auto my-8"></div>
            
            <p className="text-2xl font-serif font-bold">By Appointment. By Reservation.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-gradient-to-b from-[#0A0E27] to-[#0D1235]">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-[3rem] overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A0E27 0%, #1a1042 100%)' }}>
            <div className="absolute inset-0 opacity-30">
              <img 
                src="https://readdy.ai/api/search-image?query=mystical-sacred-flame-burning-on-altar-golden-light-spiritual-atmosphere-dark-background-ceremonial-fire-warm-glow-ancient-ritual-setting&width=1200&height=600&seq=cta1&orientation=landscape" 
                alt="Sacred Flame" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            
            <div className="relative z-10 py-24 px-12 text-center">
              <div className="w-20 h-20 mx-auto mb-12 flex items-center justify-center">
                <i className="ri-fire-fill text-7xl text-[#D4AF37]"></i>
              </div>
              
              <h2 className="font-serif text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-[#D4AF37] via-[#F5D76E] to-[#B8956A] bg-clip-text text-transparent">
                Experience Sacred Ember
              </h2>
              
              <p className="font-serif italic text-3xl text-[#F5F1E8] mb-12">
                Where atmosphere meets intention.
              </p>
              
              <a href="#contact" className="inline-block px-16 py-6 bg-[#D4AF37] text-[#0A0E27] rounded-full font-bold text-xl tracking-wide hover:bg-[#F5D76E] hover:shadow-2xl hover:shadow-[#D4AF37]/50 transition-all duration-300 cursor-pointer whitespace-nowrap mb-6">
                BOOK YOUR EXPERIENCE
              </a>
              
              <p className="text-sm text-[#B8956A] font-serif">By Appointment. By Reservation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#050814] text-[#F5F1E8] relative overflow-hidden pb-24">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'20\' viewBox=\'0 0 100 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 10h100\' stroke=\'%23D4AF37\' stroke-width=\'0.5\' fill=\'none\'/%3E%3C/svg%3E")' }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            {/* Column 1 - Logo & Info */}
            <div>
              <img src="https://static.readdy.ai/image/dadfa7b8e8bbab8ae3e8df395caac6f8/bbfc43b97cf1f83227ff7a4f04afe222.png" alt="Sacred Ember Lounge" className="h-32 w-auto mb-4" />
              <p className="text-sm text-[#B8956A] font-serif italic mb-6">Where Atmosphere, Culture, and Experience Converge</p>
              <p className="text-sm text-[#F5F1E8] mb-2">Private Members Lounge</p>
            </div>

            {/* Column 2 - Experience */}
            <div>
              <h4 className="text-sm text-[#D4AF37] uppercase tracking-widest mb-6">Experience</h4>
              <ul className="space-y-3">
                <li><a href="#experience" className="text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">The Experience</a></li>
                <li><a href="#atmosphere" className="text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">Atmosphere</a></li>
                <li><a href="#menu" className="text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">Menu</a></li>
                <li><a href="#entertainment" className="text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">Entertainment</a></li>
                <li><a href="#gallery" className="text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">Gallery</a></li>
                <li><a href="#membership" className="text-[#F5F1E8] hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">Membership</a></li>
              </ul>
            </div>

            {/* Column 3 - Connect */}
            <div>
              <h4 className="text-sm text-[#D4AF37] uppercase tracking-widest mb-6">Connect</h4>
              <ul className="space-y-3">
                <li className="text-[#F5F1E8]">info@sacredemberlounge.com</li>
                <li className="text-[#F5F1E8]">844-712-2810 ext. 3</li>
                <li className="text-[#F5F1E8]">2935 E. Main Street, Unit 91133<br />Bexley, Ohio 43209, USA</li>
              </ul>
            </div>

            {/* Column 4 - Hours */}
            <div>
              <h4 className="text-sm text-[#D4AF37] uppercase tracking-widest mb-6">Visit</h4>
              <ul className="space-y-3 text-[#F5F1E8] font-serif">
                <li>By Appointment</li>
                <li>By Reservation</li>
                <li>Private Events Available</li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-[#D4AF37]/30 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <p className="text-sm text-[#F5F1E8]/60">© 2025 Sacred Ember Lounge™. All rights reserved.</p>
              
              <div className="flex items-center gap-6">
                <a href="#" className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0E27] transition-all duration-300 cursor-pointer flex-shrink-0">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0E27] transition-all duration-300 cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0E27] transition-all duration-300 cursor-pointer">
                  <i className="ri-twitter-x-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0E27] transition-all duration-300 cursor-pointer">
                  <i className="ri-youtube-fill text-lg"></i>
                </a>
              </div>
              
              <div className="flex items-center gap-6 text-sm">
                <a href="#" className="text-[#F5F1E8]/60 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">Privacy</a>
                <a href="#" className="text-[#F5F1E8]/60 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">Terms</a>
                <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-[#F5F1E8]/60 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
